import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type {
  DialerMessageField,
  DialerSettingsDTO,
  DialerVariable,
  UpdateDialerSettingsRequest,
} from "@tw-pulse/shared";
import { DIALER_VARIABLES, dialerVariablesFor, interpolateDialerMessage } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/Button";

/**
 * Dialer → Call messages settings. The three tenant-editable spoken lines
 * (screener, voicemail, handoff). Variables are inserted from a picker — not
 * free-typed — mirroring AgentFlow's flow-variable approach.
 */

type LoadState =
  | { status: "loading" }
  | { status: "ready"; settings: DialerSettingsDTO }
  | { status: "error"; message: string };

const SAMPLE_VALUES: Record<string, string> = Object.fromEntries(
  DIALER_VARIABLES.map((v) => [v.key, v.sampleValue]),
);

const labelCls = "block text-[11px] font-medium uppercase tracking-wider text-zinc-500";

export function DialerSettingsPage() {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  // Working draft (effective values from the API; clearing a field restores default).
  const [screenerMsg, setScreenerMsg] = useState("");
  const [voicemailMsg, setVoicemailMsg] = useState("");
  const [handoffMsg, setHandoffMsg] = useState("");

  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function hydrate(s: DialerSettingsDTO) {
    setState({ status: "ready", settings: s });
    setScreenerMsg(s.screenerMsg);
    setVoicemailMsg(s.voicemailMsg);
    setHandoffMsg(s.handoffMsg);
  }

  useEffect(() => {
    let cancelled = false;
    void api.dialer
      .getSettings()
      .then((s) => {
        if (!cancelled) hydrate(s);
      })
      .catch((e) => {
        if (!cancelled) {
          setState({
            status: "error",
            message: e instanceof ApiClientError ? e.message : "Failed to load settings",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const saved = state.status === "ready" ? state.settings : null;
  const dirty =
    !!saved &&
    (screenerMsg !== saved.screenerMsg ||
      voicemailMsg !== saved.voicemailMsg ||
      handoffMsg !== saved.handoffMsg);

  async function save() {
    if (!dirty || !saved) return;
    setBusy(true);
    setErr(null);
    try {
      const body: UpdateDialerSettingsRequest = {};
      if (screenerMsg !== saved.screenerMsg) body.screenerMsg = screenerMsg;
      if (voicemailMsg !== saved.voicemailMsg) body.voicemailMsg = voicemailMsg;
      if (handoffMsg !== saved.handoffMsg) body.handoffMsg = handoffMsg;
      hydrate(await api.dialer.updateSettings(body));
      setSavedAt(Date.now());
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <Link
          to="/dialer"
          className="text-[12.5px] font-medium text-zinc-500 transition hover:text-zinc-800 dark:hover:text-zinc-300"
        >
          ← Screened Calls
        </Link>
        <p className="mt-2 text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
          Dialer
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          Settings
        </h1>
        <p className="mt-1 max-w-2xl text-[13.5px] text-zinc-600 dark:text-zinc-400">
          The spoken lines you control. Voice, barge-in copy, and answer detection are
          platform-managed. Insert variables from the picker; clear a field to restore the platform
          default.
        </p>
      </header>

      {state.status === "loading" && <PageSkeleton />}

      {state.status === "error" && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {state.message}
        </div>
      )}

      {state.status === "ready" && (
        <form
          className="max-w-2xl space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <MessageField
            label="Screener message"
            hint="Played first when a call screener answers. If no one then picks up, the voicemail message plays."
            field="screenerMsg"
            value={screenerMsg}
            onChange={setScreenerMsg}
          />
          <MessageField
            label="Voicemail message"
            hint="Left on voicemail, and after the screener message when no one answers."
            field="voicemailMsg"
            value={voicemailMsg}
            onChange={setVoicemailMsg}
          />
          <MessageField
            label="Handoff message"
            hint="Played to a live person just before they're connected to your handoff."
            field="handoffMsg"
            value={handoffMsg}
            onChange={setHandoffMsg}
          />

          <div className="flex flex-wrap items-center gap-3 border-t border-zinc-200/70 pt-4 dark:border-zinc-800/60">
            <Button type="submit" disabled={busy || !dirty}>
              {busy ? "Saving…" : "Save"}
            </Button>
            {savedAt && !dirty && (
              <span className="text-[12px] font-medium text-emerald-600 dark:text-emerald-400">
                Saved
              </span>
            )}
            {err && (
              <span role="alert" className="text-[12px] text-red-600 dark:text-red-400">
                {err}
              </span>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

const textareaCls =
  "block w-full resize-y rounded-md border border-zinc-200 bg-white px-3 py-2 text-[13.5px] leading-relaxed text-zinc-900 placeholder:text-zinc-400 " +
  "focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 " +
  "dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600";

/** Label + insert-variable picker + textarea + live preview. */
function MessageField({
  label,
  hint,
  field,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  field: DialerMessageField;
  value: string;
  onChange: (s: string) => void;
}) {
  const { me } = useAuth();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const variables = useMemo(() => dialerVariablesFor(field), [field]);

  const previewValues = useMemo(() => {
    const realBusinessName = me?.tenant.businessName?.trim() || me?.tenant.name?.trim();
    return realBusinessName ? { ...SAMPLE_VALUES, businessName: realBusinessName } : SAMPLE_VALUES;
  }, [me]);

  function insertVariable(key: string) {
    const ta = textareaRef.current;
    const token = `{${key}}`;
    if (!ta) {
      onChange(value + token);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    onChange(value.slice(0, start) + token + value.slice(end));
    setTimeout(() => {
      ta.focus();
      const pos = start + token.length;
      ta.selectionStart = ta.selectionEnd = pos;
    }, 0);
  }

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className={labelCls}>{label}</span>
        <InsertVariableMenu variables={variables} onInsert={insertVariable} />
      </div>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        maxLength={600}
        className={textareaCls}
      />
      <p className="mt-1 text-[11px] text-zinc-500">{hint}</p>

      <div className="mt-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-800 dark:bg-zinc-900/40">
        <p className="mb-1 text-[10.5px] font-medium uppercase tracking-wider text-zinc-500">
          Preview
        </p>
        <p className="text-[13px] leading-relaxed text-zinc-800 dark:text-zinc-200">
          {value.trim() === "" ? (
            <span className="text-zinc-400">Empty</span>
          ) : (
            interpolateDialerMessage(value, previewValues)
          )}
        </p>
      </div>
    </div>
  );
}

/** Dropdown of the variables valid for this field. Selectable, not free-typed. */
function InsertVariableMenu({
  variables,
  onInsert,
}: {
  variables: DialerVariable[];
  onInsert: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as globalThis.Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="inline-flex h-7 items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 text-[11.5px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-300 dark:hover:bg-zinc-800/60 dark:focus-visible:ring-offset-zinc-950"
      >
        <BraceIcon />
        Insert variable
        <ChevronDownIcon />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-80 overflow-hidden rounded-md border border-zinc-200 bg-white shadow-lg dark:border-zinc-800 dark:bg-zinc-900"
        >
          <ul>
            {variables.map((v) => (
              <li key={v.key}>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onInsert(v.key);
                    setOpen(false);
                  }}
                  title={v.description}
                  className="block w-full px-3 py-2 text-left transition-colors hover:bg-zinc-50 focus-visible:bg-zinc-50 focus-visible:outline-none dark:hover:bg-zinc-800/60 dark:focus-visible:bg-zinc-800/60"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11.5px] text-brand-600 dark:text-brand-300">
                      {"{" + v.key + "}"}
                    </span>
                    <span className="text-[12px] text-zinc-700 dark:text-zinc-300">{v.label}</span>
                  </div>
                  <p className="mt-0.5 text-[11px] text-zinc-500">{v.description}</p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className="max-w-2xl space-y-5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="space-y-2">
          <div className="h-3 w-32 animate-pulse rounded bg-zinc-100 dark:bg-zinc-800/40" />
          <div className="h-16 animate-pulse rounded-md bg-zinc-100 dark:bg-zinc-800/40" />
        </div>
      ))}
    </div>
  );
}

function BraceIcon() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true">
      <path
        d="M8 4c-2 0-2 3-2 4s0 2-2 2c2 0 2 1 2 2s0 4 2 4M16 4c2 0 2 3 2 4s0 2 2 2c-2 0-2 1-2 2s0 4-2 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true">
      <path
        d="m6 9 6 6 6-6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
